import {useEffect} from "react";
import {dropStyles} from "./card-panel.css";
import {by} from "../../lib/sort";
import ORDERS from "../card-order/order-info";
import {cardHighlightActions} from "../card-highlight/useCardHighlight";
import {XText, cx, useDelayedTrigger, useToggle} from "@cdx/common";
import {useDropZone} from "@codecks/dnd";
import {completeOnboardingStep, ONBOARDING_STEPS} from "../onboarding/onboarding-utils";
import {Box, Col, Row} from "@cdx/ds";
import {makeScrollable} from "@cdx/ds/utils/makeScrollable";
import {transitions} from "@cdx/ds/css/decoration.css";
import {useCardResizeArea} from "../../components/Card/dragged-card-resize";
import {dropChecksByType, getCardOrders} from "./manually-ordererd-card-utils";
import {api} from "../../lib/api";
import {StickyTopAwareStickyBox} from "../card-container/card-container-utils";

export const allCardsWithOrderKey = ({
  cards,
  orderInfo,
  orderKeyAsScalar,
  projects,
  root,
  notifications,
}) => {
  if (orderInfo === null) {
    // there's only one order key in the compact view
    return true;
  }
  const {prepareExtractData, extractKey, keyToScalar = (k) => k} = orderInfo;
  const preparedData = prepareExtractData && prepareExtractData({projects, root, notifications});
  return cards.every((c) => {
    const keyAsValue = extractKey(c, preparedData);
    if (Array.isArray(keyAsValue)) {
      return keyAsValue.some((k) => keyToScalar(k) === orderKeyAsScalar);
    } else {
      return keyToScalar(keyAsValue) === orderKeyAsScalar;
    }
  });
};

const useDropTarget = ({
  orderKeyAsValue,
  orderInfo,
  bag,
  orderKeyAsScalar,
  onDropError,
  onDrop: customOnDrop,
}) => {
  const {onDrop, cannotDrop} = orderInfo || {};
  const canDropCard = (item) => {
    const {projects, root, notifications} = bag;

    if (!onDrop) return false;

    const {cardInfos} = item.data;
    const cards = cardInfos.map(({getCard}) => getCard());

    if (onDropError) {
      const msg = onDropError(cardInfos);
      if (msg) return false;
    }

    if (allCardsWithOrderKey({cards, orderInfo, orderKeyAsScalar, projects, root, notifications})) {
      return false;
    }
    return cardInfos.every(({getCard}) => {
      return !(cannotDrop && cannotDrop(orderKeyAsValue, getCard()));
    });
  };

  const handleCardDrop = async ({item}) => {
    if (!canDropCard(item)) return;
    const {cardInfos} = item.data;
    const validCards = cardInfos
      .map(({getCard}) => getCard())
      .filter((card) => !(cannotDrop && cannotDrop(orderKeyAsValue, card)));
    if (validCards.length > 0) {
      completeOnboardingStep(ONBOARDING_STEPS.dragAndDropCard);
      if (customOnDrop) await customOnDrop(validCards);
      await onDrop(validCards, orderKeyAsValue);
      cardHighlightActions.setCardHighlights(new Set(validCards.map((c) => c.id)));
    }
  };

  const {
    dragItem,
    ref: dropRef,
    isOver,
  } = useDropZone({
    type: "CARD",
    disabled: !onDrop,
    onDrop: handleCardDrop,
  });

  const canDrop = dragItem && canDropCard(dragItem);

  return {dragItem, dropRef, isOver, canDrop};
};

export const useCardDropTarget = useDropTarget;

const SideDragZone = ({orderKeyAsValue, primaryOrderInfo, orderKeyAsScalar, bag}) => {
  const {getLabel} = primaryOrderInfo;
  const {isOver, canDrop, dropRef} = useDropTarget({
    orderKeyAsValue,
    orderKeyAsScalar,
    orderInfo: primaryOrderInfo,
    bag,
  });
  const getType = () => {
    if (!canDrop) return "cannotDrop";
    return isOver ? "isOver" : "canDrop";
  };
  return (
    <Row
      bg="foreground"
      gradientBg="horizontal"
      rounded={4}
      pa="16px"
      className={cx(dropStyles.zone.types[getType()], transitions.colors)}
      ref={dropRef}
    >
      {getLabel(orderKeyAsValue, {
        arenaCtx: bag.arenaCtx,
        mode: "dropZone",
        root: bag.root,
        onLight: false,
      })}
    </Row>
  );
};

const getManualOrderInfo = (bag) => {
  const {root, arenaCtx} = bag;

  const cardIdToOrderAndLabel = getCardOrders({
    account: root.account,
    arenaCtx,
  });

  const modelId = arenaCtx.model.$meta.get("id", null);
  const context = arenaCtx.type;

  const {manualOrderLabels} = arenaCtx.model;
  const dropChecks = dropChecksByType[arenaCtx.type];
  return {
    onDrop: async (cards, orderKey) => {
      const cardIds = cards.map((c) => c.id);
      await dropChecks.handleDroppedCards(modelId, cardIds);

      return api.mutate.cardOrders.addAfter({
        targetId: null,
        cardIds: cardIds,
        context,
        label: orderKey,
      });
    },
    allKeys: () => manualOrderLabels,
    allKeysAreSorted: true,
    toSortKey: (k) => k,
    keyToScalar: (k) => k,
    getLabel: (k, {onLight}) => (
      <XText size={1} lineHeight="none" color={onLight ? "gray700" : "gray100"}>
        {k ?? "Default Zone"}
      </XText>
    ),
    extractKey: (c) => {
      const exist = cardIdToOrderAndLabel[c.id];
      // if no label was set, pick the first lane
      return exist ? exist.label : manualOrderLabels[0] ?? null;
    },
  };
};

const CardDropSideBar = ({bag, cardOrder}) => {
  const [show, {on, off}] = useToggle();
  const {root} = bag;
  const primaryOrderInfo =
    cardOrder.prop === "manual" ? getManualOrderInfo(bag) : ORDERS[cardOrder.prop];
  const {allKeysAreSorted, onDrop, allKeys, toSortKey, keyToScalar = (k) => k} = primaryOrderInfo;

  const trigger = useDelayedTrigger();
  useEffect(() => {
    if (onDrop) {
      trigger.fire(on, 50);
    } else {
      off();
      trigger.cancel();
    }
  }, [onDrop, trigger, on, off]);

  const nodeRef = useCardResizeArea({cardScale: 0.25, reach: 100, disabled: !show});

  if (!show) return null;
  const keysAsValues = allKeysAreSorted
    ? allKeys(bag)
    : [...allKeys(bag)].sort(by((keyAsValue) => toSortKey(keyAsValue, root)));
  if (cardOrder.isReversed) keysAsValues.reverse();

  return (
    <Box
      absolute
      right={0}
      top={0}
      bottom={0}
      width="15rem"
      zIndex={3}
      colorTheme="gray750"
      bg="foreground"
      ref={nodeRef}
    >
      <Col styleChild maxHeight="100%" width="100%" className={makeScrollable()} px="16px">
        <StickyTopAwareStickyBox>
          <Col sp="8px" py="16px">
            {keysAsValues.map((keyAsValue) => {
              const keyAsScalar = keyToScalar(keyAsValue);
              return (
                <SideDragZone
                  key={keyAsScalar}
                  orderKeyAsValue={keyAsValue}
                  primaryOrderInfo={primaryOrderInfo}
                  orderKeyAsScalar={keyAsScalar}
                  bag={bag}
                />
              );
            })}
          </Col>
        </StickyTopAwareStickyBox>
      </Col>
    </Box>
  );
};

export default CardDropSideBar;
